import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { LogOut, Plus, Trash2, Edit3, Check, X, Upload } from "lucide-react";

const CATEGORIES = [
  { slug: "politics", name: "రాజకీయం" },
  { slug: "district", name: "జిల్లా వార్తలు" },
  { slug: "agriculture", name: "వ్యవసాయం" },
  { slug: "crime", name: "క్రైమ్" },
  { slug: "cinema", name: "సినిమా/వినోదం" },
  { slug: "special", name: "ప్రత్యేక కథనాలు" },
];

const emptyArticle = { title: "", summary: "", body: "", category: "politics", cover_image: "", is_breaking: false, is_featured: false, author: "నల్లమల వాయిస్", published: true };

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);
  const [videos, setVideos] = useState([]);
  const [comments, setComments] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyArticle);
  const [uploading, setUploading] = useState(false);

  const [videoForm, setVideoForm] = useState({ title: "", youtube_url: "", description: "" });

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/admin/login");
  }, [user, loading, navigate]);

  const loadAll = async () => {
    try {
      const [a, v, c, ct] = await Promise.all([
        api.get("/admin/articles"),
        api.get("/videos"),
        api.get("/admin/comments"),
        api.get("/admin/contacts"),
      ]);
      setArticles(a.data); setVideos(v.data); setComments(c.data); setContacts(ct.data);
    } catch { /* handled */ }
  };
  useEffect(() => { if (user?.role === "admin") loadAll(); }, [user]);

  const openCreate = () => { setEditing(null); setForm(emptyArticle); setDialogOpen(true); };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ ...emptyArticle, ...a });
    setDialogOpen(true);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const saveArticle = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title & body required"); return; }
    try {
      if (editing) {
        await api.patch(`/admin/articles/${editing.id}`, form);
        toast.success("Article updated");
      } else {
        await api.post("/admin/articles", form);
        toast.success("Article published");
      }
      setDialogOpen(false); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try { await api.delete(`/admin/articles/${id}`); toast.success("Deleted"); loadAll(); } catch { toast.error("Failed"); }
  };

  const createVideo = async (e) => {
    e.preventDefault();
    if (!videoForm.title.trim() || !videoForm.youtube_url.trim()) { toast.error("Title & URL required"); return; }
    try {
      await api.post("/admin/videos", videoForm);
      toast.success("Video added");
      setVideoForm({ title: "", youtube_url: "", description: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteVideo = async (id) => {
    if (!window.confirm("Delete video?")) return;
    try { await api.delete(`/admin/videos/${id}`); loadAll(); } catch {}
  };

  const moderateComment = async (id, status) => {
    try { await api.patch(`/admin/comments/${id}`, { status }); toast.success(status); loadAll(); } catch {}
  };
  const deleteComment = async (id) => {
    try { await api.delete(`/admin/comments/${id}`); loadAll(); } catch {}
  };

  if (loading || !user) return <div className="p-10 font-body-te">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#FDFCF8] nv-selectable">
      <header className="bg-white border-b border-[#E5E4DC] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#2E7D32] text-white flex items-center justify-center font-heading-te">న</div>
            <div>
              <div className="font-heading-te text-lg font-bold">Admin · నల్లమల వాయిస్</div>
              <div className="text-xs text-[#4A4A4A]">{user.email}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm underline text-[#2E7D32]" data-testid="admin-view-site">View Site</Link>
            <Button variant="outline" size="sm" onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs defaultValue="articles" data-testid="admin-tabs">
          <TabsList>
            <TabsTrigger value="articles" data-testid="tab-articles">Articles ({articles.length})</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">Videos ({videos.length})</TabsTrigger>
            <TabsTrigger value="comments" data-testid="tab-comments">Comments ({comments.filter(c=>c.status==="pending").length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts ({contacts.length})</TabsTrigger>
          </TabsList>

          {/* Articles */}
          <TabsContent value="articles" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading-te text-2xl font-bold">Articles</h2>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openCreate} className="bg-[#2E7D32] hover:bg-[#1B5E20]" data-testid="new-article-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Article
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>{editing ? "Edit Article" : "New Article"}</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Title *</Label>
                      <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="article-title-input" />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="article-category-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Summary</Label>
                      <Textarea rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="article-summary-input" />
                    </div>
                    <div>
                      <Label>Body * (paragraphs separated by blank lines)</Label>
                      <Textarea rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="article-body-input" />
                    </div>
                    <div>
                      <Label>Cover Image</Label>
                      <div className="flex items-center gap-3">
                        <Input placeholder="/api/uploads/... or https://..." value={form.cover_image || ""} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="article-cover-input" />
                        <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-[#E5E4DC] hover:bg-gray-50 text-sm">
                          <Upload className="w-4 h-4" /> {uploading ? "..." : "Upload"}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} data-testid="article-cover-upload" />
                        </label>
                      </div>
                      {form.cover_image && (
                        <img src={assetUrl(form.cover_image)} alt="cover" className="mt-2 w-32 h-20 object-cover border border-[#E5E4DC]" />
                      )}
                    </div>
                    <div>
                      <Label>Author</Label>
                      <Input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex items-center gap-2">
                        <Switch checked={form.is_breaking} onCheckedChange={(v) => setForm({ ...form, is_breaking: v })} data-testid="switch-breaking" />
                        <Label>Breaking</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} data-testid="switch-featured" />
                        <Label>Featured</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
                        <Label>Published</Label>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={saveArticle} className="bg-[#2E7D32] hover:bg-[#1B5E20]" data-testid="save-article-btn">
                      {editing ? "Update" : "Publish"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="border border-[#E5E4DC] bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3">Title</th><th className="p-3">Category</th>
                    <th className="p-3">Status</th><th className="p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((a) => (
                    <tr key={a.id} className="border-t border-[#E5E4DC]" data-testid={`admin-article-row-${a.id}`}>
                      <td className="p-3 font-heading-te font-semibold max-w-md truncate">{a.title}</td>
                      <td className="p-3">{CATEGORIES.find(c => c.slug === a.category)?.name}</td>
                      <td className="p-3 space-x-1">
                        {a.is_breaking && <Badge variant="destructive">Breaking</Badge>}
                        {a.is_featured && <Badge className="bg-[#2E7D32]">Featured</Badge>}
                        {!a.published && <Badge variant="outline">Draft</Badge>}
                      </td>
                      <td className="p-3 flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(a)}><Edit3 className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => deleteArticle(a.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </td>
                    </tr>
                  ))}
                  {articles.length === 0 && (
                    <tr><td colSpan={4} className="p-8 text-center text-[#4A4A4A]">No articles yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Videos */}
          <TabsContent value="videos" className="mt-6">
            <form onSubmit={createVideo} className="border border-[#E5E4DC] bg-white p-5 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="video-form">
              <Input placeholder="Video title" value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} data-testid="video-title-input" />
              <Input placeholder="YouTube URL" value={videoForm.youtube_url} onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })} className="md:col-span-2" data-testid="video-url-input" />
              <Button type="submit" className="bg-[#2E7D32] hover:bg-[#1B5E20]" data-testid="video-submit-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
              <Textarea placeholder="Description (optional)" value={videoForm.description} onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })} className="md:col-span-4" rows={2} />
            </form>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {videos.map((v) => (
                <div key={v.id} className="border border-[#E5E4DC] bg-white">
                  <div className="aspect-video">
                    <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${v.youtube_id}`} title={v.title} allowFullScreen />
                  </div>
                  <div className="p-3 flex justify-between items-start gap-2">
                    <div className="font-heading-te font-bold text-sm line-clamp-2">{v.title}</div>
                    <Button size="sm" variant="outline" onClick={() => deleteVideo(v.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Comments */}
          <TabsContent value="comments" className="mt-6">
            <div className="space-y-3">
              {comments.map((c) => (
                <div key={c.id} className="border border-[#E5E4DC] bg-white p-4" data-testid={`comment-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-heading-te font-bold">{c.author_name}</span>
                        <Badge variant={c.status === "approved" ? "default" : c.status === "pending" ? "secondary" : "outline"} className={c.status === "approved" ? "bg-[#2E7D32]" : ""}>{c.status}</Badge>
                      </div>
                      <div className="text-xs text-[#4A4A4A] mb-1">Article: {c.article_id.slice(0, 8)}... · {new Date(c.created_at).toLocaleString()}</div>
                      <p className="font-body-te text-sm">{c.body}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {c.status !== "approved" && <Button size="sm" onClick={() => moderateComment(c.id, "approved")} className="bg-[#2E7D32] hover:bg-[#1B5E20]" data-testid={`approve-${c.id}`}><Check className="w-4 h-4" /></Button>}
                      {c.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => moderateComment(c.id, "rejected")} data-testid={`reject-${c.id}`}><X className="w-4 h-4" /></Button>}
                      <Button size="sm" variant="outline" onClick={() => deleteComment(c.id)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                </div>
              ))}
              {comments.length === 0 && <div className="text-center py-10 text-[#4A4A4A]">No comments.</div>}
            </div>
          </TabsContent>

          {/* Contacts */}
          <TabsContent value="contacts" className="mt-6">
            <div className="border border-[#E5E4DC] bg-white">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-3">Name</th><th className="p-3">Email/Phone</th><th className="p-3">Message</th><th className="p-3">When</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} className="border-t border-[#E5E4DC]">
                      <td className="p-3 font-semibold">{c.name}</td>
                      <td className="p-3 text-xs">{c.email}<br />{c.phone}</td>
                      <td className="p-3 max-w-md">{c.message}</td>
                      <td className="p-3 text-xs">{new Date(c.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                  {contacts.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#4A4A4A]">No messages yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
